import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Download, Loader2, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { useToast } from '../ui/use-toast';

import { BrowserFallback } from './browser/BrowserFallback';
import { BrowserLibraryDialog } from './browser/BrowserLibraryDialog';
import { BrowserPasswordManagerDialog } from './browser/BrowserPasswordManagerDialog';
import { BrowserTabStrip } from './browser/BrowserTabStrip';
import { BrowserToolbar } from './browser/BrowserToolbar';
import { useBrowserData } from './browser/useBrowserData';
import { useBrowserTabs } from './browser/useBrowserTabs';
import type { BrowserViewportEventDetail } from './browser/types';

interface WebBrowserProps {
  windowId: string;
  onClose?: () => void;
}

const DEFAULT_HOME_URL = 'https://duckduckgo.com/';
const WINDOW_VIEWPORT_EVENT = 'mapleos:window-viewport-change';

const normalizeTarget = (input: string) => {
  const raw = input.trim();

  if (!raw) {
    return DEFAULT_HOME_URL;
  }

  if (/^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(raw)) {
    const parsed = new URL(raw);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      throw new Error('Only http(s) URLs are supported');
    }
    return parsed.toString();
  }

  if (!raw.includes('.') || /\s/.test(raw)) {
    return `https://duckduckgo.com/?q=${encodeURIComponent(raw)}`;
  }

  return new URL(`https://${raw}`).toString();
};

export const WebBrowser: React.FC<WebBrowserProps> = ({ windowId }) => {
  const { toast } = useToast();
  const surfaceRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const visibleRef = useRef(true);
  const [addressValue, setAddressValue] = useState(DEFAULT_HOME_URL);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isSecurityDetailsOpen, setIsSecurityDetailsOpen] = useState(false);
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const [isPasswordManagerOpen, setIsPasswordManagerOpen] = useState(false);
  const [dialogPortalContainer, setDialogPortalContainer] = useState<HTMLElement | null>(null);
  const {
    isSupported,
    state,
    activeTab,
    browserTheme,
    homepage,
    privateMode,
    lastDownloadEvent,
    clearLastDownloadEvent,
    createTab,
    closeTab,
    activateTab,
    navigateActiveTab,
    goBack,
    goForward,
    reload,
    stop,
    setBounds,
    setBrowserTheme,
    setHomepage,
    setPrivateMode,
  } = useBrowserTabs(windowId);

  const {
    bookmarks,
    historyEntries,
    currentBookmark,
    toggleBookmarkForActiveTab,
    removeBookmark,
    clearHistory,
    currentOrigin,
    hasPasswordVault,
    isPasswordVaultUnlocked,
    savedPasswords,
    currentSitePasswords,
    createPasswordVault,
    unlockPasswordVault,
    lockPasswordVault,
    savePassword,
    deletePassword,
    fillPassword,
  } = useBrowserData({
    activeTab,
    privateMode,
    windowId,
  });

  useEffect(() => {
    setAddressValue(activeTab?.url || homepage);
  }, [activeTab?.url, homepage]);

  useEffect(() => {
    if (!lastDownloadEvent) {
      return;
    }

    if (lastDownloadEvent.status === 'completed' && lastDownloadEvent.vfsPath) {
      const path = lastDownloadEvent.vfsPath;
      const parentPath = path.includes('\\') ? path.slice(0, path.lastIndexOf('\\')) : path;
      window.dispatchEvent(new CustomEvent('fileSystemChanged', { detail: { path: parentPath } }));
      toast({
        title: 'Download saved to MapleOS',
        description: path,
      });
      clearLastDownloadEvent();
      return;
    }

    if (lastDownloadEvent.status === 'failed') {
      toast({
        title: 'Download failed',
        description: lastDownloadEvent.error || 'Navigator could not save this download.',
        variant: 'destructive',
      });
      clearLastDownloadEvent();
    }
  }, [clearLastDownloadEvent, lastDownloadEvent, toast]);

  useEffect(() => {
    const nextContainer = rootRef.current?.closest('[data-window-id]') as HTMLElement | null;
    setDialogPortalContainer(nextContainer || null);
  }, [windowId]);

  const syncSurfaceBounds = useCallback((forceVisible?: boolean) => {
    const node = surfaceRef.current;
    if (!node) {
      return;
    }

    const rect = node.getBoundingClientRect();
    const bounds = {
      x: Math.floor(rect.left),
      y: Math.floor(rect.top),
      width: Math.max(0, Math.floor(rect.width)),
      height: Math.max(0, Math.floor(rect.height)),
    };

    const visible = !isSettingsOpen && !isSecurityDetailsOpen && !isLibraryOpen && !isPasswordManagerOpen && (forceVisible ?? visibleRef.current) && bounds.width > 0 && bounds.height > 0;
    setBounds(bounds, visible).catch((error) => {
      console.error('Failed to sync Navigator bounds:', error);
    });
  }, [isLibraryOpen, isPasswordManagerOpen, isSecurityDetailsOpen, isSettingsOpen, setBounds]);

  const hideBrowserSurface = useCallback(() => {
    setBounds({ x: 0, y: 0, width: 0, height: 0 }, false).catch(() => {});
  }, [setBounds]);

  const handleSettingsOpenChange = useCallback((open: boolean) => {
    if (open) {
      hideBrowserSurface();
    }
    setIsSettingsOpen(open);
  }, [hideBrowserSurface]);

  const handleSecurityDetailsOpenChange = useCallback((open: boolean) => {
    if (open) {
      hideBrowserSurface();
    }
    setIsSecurityDetailsOpen(open);
  }, [hideBrowserSurface]);

  const handleLibraryOpenChange = useCallback((open: boolean) => {
    if (open) {
      hideBrowserSurface();
    }
    setIsLibraryOpen(open);
  }, [hideBrowserSurface]);

  const handlePasswordManagerOpenChange = useCallback((open: boolean) => {
    if (open) {
      hideBrowserSurface();
    }
    setIsPasswordManagerOpen(open);
  }, [hideBrowserSurface]);

  const handleOpenLibrary = useCallback(() => {
    hideBrowserSurface();
    setIsSecurityDetailsOpen(false);
    setIsPasswordManagerOpen(false);
    setIsLibraryOpen(true);
    setIsSettingsOpen(false);
  }, [hideBrowserSurface]);

  const handleOpenPasswordManager = useCallback(() => {
    hideBrowserSurface();
    setIsSecurityDetailsOpen(false);
    setIsLibraryOpen(false);
    setIsPasswordManagerOpen(true);
    setIsSettingsOpen(false);
  }, [hideBrowserSurface]);

  useEffect(() => {
    if (!isSupported) {
      return;
    }

    const handleViewportChange = (event: Event) => {
      const customEvent = event as CustomEvent<BrowserViewportEventDetail>;
      if (customEvent.detail?.windowId !== windowId) {
        return;
      }

      visibleRef.current = Boolean(customEvent.detail.visible);
      requestAnimationFrame(() => syncSurfaceBounds(customEvent.detail.visible));
    };

    const resizeObserver = new ResizeObserver(() => {
      syncSurfaceBounds();
    });

    const node = surfaceRef.current;
    if (node) {
      resizeObserver.observe(node);
    }

    const handleWindowResize = () => syncSurfaceBounds();

    window.addEventListener(WINDOW_VIEWPORT_EVENT, handleViewportChange as EventListener);
    window.addEventListener('resize', handleWindowResize);
    requestAnimationFrame(() => syncSurfaceBounds());

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener(WINDOW_VIEWPORT_EVENT, handleViewportChange as EventListener);
      window.removeEventListener('resize', handleWindowResize);
      setBounds({ x: 0, y: 0, width: 0, height: 0 }, false).catch(() => {});
    };
  }, [isSupported, setBounds, syncSurfaceBounds, windowId]);

  useEffect(() => {
    if (isSupported) {
      requestAnimationFrame(() => syncSurfaceBounds());
    }
  }, [isSupported, isLibraryOpen, isPasswordManagerOpen, isSecurityDetailsOpen, isSettingsOpen, state?.activeTabId, state?.tabs.length, syncSurfaceBounds]);

  const downloadLabel = useMemo(() => {
    if (!lastDownloadEvent) {
      return null;
    }

    if (lastDownloadEvent.status === 'progress') {
      return `Downloading ${lastDownloadEvent.filename}`;
    }

    if (lastDownloadEvent.status === 'started') {
      return `Starting download: ${lastDownloadEvent.filename}`;
    }

    return null;
  }, [lastDownloadEvent]);

  const handleSubmit = useCallback(async () => {
    try {
      await navigateActiveTab(normalizeTarget(addressValue));
    } catch (error) {
      toast({
        title: 'Invalid address',
        description: error instanceof Error ? error.message : 'Navigator could not understand that address.',
        variant: 'destructive',
      });
    }
  }, [addressValue, navigateActiveTab, toast]);

  const handleToggleBookmark = useCallback(async () => {
    const bookmarked = await toggleBookmarkForActiveTab();
    if (!activeTab?.url) {
      return;
    }

    toast({
      title: bookmarked ? 'Bookmark saved' : 'Bookmark removed',
      description: bookmarked ? activeTab.url : 'The current page was removed from your bookmarks.',
    });
  }, [activeTab?.url, toast, toggleBookmarkForActiveTab]);

  const navigateToUrl = useCallback((url: string) => {
    setAddressValue(url);
    navigateActiveTab(url).catch(() => {});
  }, [navigateActiveTab]);

  const handleTogglePrivateMode = useCallback(async () => {
    const nextMode = !privateMode;
    await setPrivateMode(nextMode);
    toast({
      title: nextMode ? 'Private mode enabled' : 'Private mode disabled',
      description: nextMode
        ? 'Navigator is now using a private in-memory browsing session for this window.'
        : 'Navigator returned to the standard browsing session for this window.',
    });
  }, [privateMode, setPrivateMode, toast]);

  if (!isSupported) {
    return <BrowserFallback />;
  }

  return (
    <div ref={rootRef} className="flex h-full min-h-0 flex-col bg-background text-foreground">
      <BrowserTabStrip
        tabs={state?.tabs || []}
        activeTabId={state?.activeTabId || null}
        privateMode={privateMode}
        onActivate={activateTab}
        onClose={closeTab}
        onNewTab={() => createTab()}
      />

      <BrowserToolbar
        activeTab={activeTab}
        addressValue={addressValue}
        browserTheme={browserTheme}
        homepage={homepage}
        isSettingsOpen={isSettingsOpen}
        isSecurityDetailsOpen={isSecurityDetailsOpen}
        isPrivateMode={privateMode}
        isCurrentPageBookmarked={Boolean(currentBookmark)}
        portalContainer={dialogPortalContainer}
        onAddressChange={setAddressValue}
        onThemeChange={setBrowserTheme}
        onHomepageChange={setHomepage}
        onSettingsOpenChange={handleSettingsOpenChange}
        onSecurityDetailsOpenChange={handleSecurityDetailsOpenChange}
        onSubmit={handleSubmit}
        onBack={goBack}
        onForward={goForward}
        onReload={reload}
        onStop={stop}
        onHome={() => {
          setAddressValue(homepage);
          navigateActiveTab(homepage).catch(() => {});
        }}
        onOpenLibrary={handleOpenLibrary}
        onOpenPasswordManager={handleOpenPasswordManager}
        onToggleBookmark={() => handleToggleBookmark().catch(() => {})}
        onTogglePrivateMode={() => handleTogglePrivateMode().catch(() => {})}
      />

      {downloadLabel ? (
        <div className="flex items-center gap-2 border-b border-border/70 bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          <Download className="h-3.5 w-3.5" />
          <span>{downloadLabel}</span>
        </div>
      ) : null}

      {privateMode ? (
        <div className="flex items-center gap-2 border-b border-primary/20 bg-primary/10 px-3 py-2 text-xs text-primary">
          <span className="font-medium">Private mode</span>
          <span>History and last-session context are not saved for this window.</span>
        </div>
      ) : null}

      <div className="relative flex-1 min-h-0 bg-muted/20">
        <div ref={surfaceRef} className="absolute inset-0" />

        {!state ? (
          <div className="absolute inset-0 flex items-center justify-center p-6">
            <Card className="flex items-center gap-3 border-border/70 bg-background/95 px-5 py-4 shadow-lg">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              <div>
                <div className="font-medium">Starting Navigator</div>
                <div className="text-sm text-muted-foreground">Electron-native browsing is initializing.</div>
              </div>
            </Card>
          </div>
        ) : null}

        {activeTab?.isLoading ? (
          <div className="pointer-events-none absolute right-4 top-4 z-10 flex items-center gap-2 rounded-full border border-border/70 bg-background/90 px-3 py-1.5 text-xs text-muted-foreground shadow-sm">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Loading {activeTab.title || 'page'}
          </div>
        ) : null}

        {activeTab?.crashed || activeTab?.lastError ? (
          <div className="absolute inset-0 z-10 flex items-center justify-center p-6">
            <Card className="max-w-lg space-y-4 border-destructive/30 bg-background/95 p-6 shadow-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 h-5 w-5 text-destructive" />
                <div>
                  <h3 className="font-semibold">This tab needs attention</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {activeTab.lastError || 'The tab renderer crashed. You can reload the tab or open a new one.'}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button type="button" onClick={() => reload().catch(() => {})}>
                  Reload Tab
                </Button>
                <Button type="button" variant="outline" onClick={() => createTab(homepage).catch(() => {})}>
                  Open New Tab
                </Button>
              </div>
            </Card>
          </div>
        ) : null}

        {state && !activeTab ? (
          <div className="absolute inset-0 flex items-center justify-center p-6">
            <Card className="max-w-md space-y-3 border-border/70 bg-background/95 p-6 text-center shadow-lg">
              <div className="flex justify-center text-primary">
                <Sparkles className="h-5 w-5" />
              </div>
              <h3 className="font-semibold">Open a tab to start browsing</h3>
              <p className="text-sm text-muted-foreground">
                Navigator uses Electron&apos;s native browser engine instead of a WASM browser runtime so modern sites can use standard APIs with much better compatibility and performance.
              </p>
            </Card>
          </div>
        ) : null}
      </div>

      <BrowserLibraryDialog
        open={isLibraryOpen}
        onOpenChange={handleLibraryOpenChange}
        bookmarks={bookmarks}
        historyEntries={historyEntries}
        onNavigate={navigateToUrl}
        onRemoveBookmark={(bookmarkId) => removeBookmark(bookmarkId).catch(() => {})}
        onClearHistory={() => clearHistory().catch(() => {})}
        portalContainer={dialogPortalContainer}
      />

      <BrowserPasswordManagerDialog
        open={isPasswordManagerOpen}
        onOpenChange={handlePasswordManagerOpenChange}
        currentOrigin={currentOrigin}
        hasPasswordVault={hasPasswordVault}
        isPasswordVaultUnlocked={isPasswordVaultUnlocked}
        savedPasswords={savedPasswords}
        currentSitePasswords={currentSitePasswords}
        portalContainer={dialogPortalContainer}
        onCreateVault={async (masterPassword) => {
          await createPasswordVault(masterPassword);
          toast({
            title: 'Password vault created',
            description: 'Navigator can now store encrypted passwords on this device.',
          });
        }}
        onUnlockVault={unlockPasswordVault}
        onLockVault={() => {
          lockPasswordVault();
          toast({
            title: 'Password vault locked',
            description: 'Saved credentials are now hidden until you unlock the vault again.',
          });
        }}
        onSavePassword={async (payload) => {
          await savePassword(payload);
          toast({
            title: 'Password saved',
            description: `Credentials saved for ${payload.origin}.`,
          });
        }}
        onDeletePassword={async (passwordId) => {
          await deletePassword(passwordId);
          toast({
            title: 'Password removed',
            description: 'The selected saved password was deleted from your vault.',
          });
        }}
        onFillPassword={fillPassword}
      />
    </div>
  );
};